import {
  isRecord,
  json,
  nullableString,
  request,
  requestNoContent,
  requireArray,
  requireNumber,
  requireString,
} from '@/lib/api'
import type { components } from '@/lib/openapi.generated'

type Schemas = components['schemas']

export type HttpConnection = Schemas['SavedHttpConnection']
export type HttpConnectionInput = Schemas['SaveHttpConnectionRequest']
export type HttpConnectionTestResult = Schemas['HttpConnectionTestResult']

function decodeConnection(raw: unknown): HttpConnection {
  if (!isRecord(raw)) throw new Error('Malformed API connection')
  return {
    connectionId: requireString(raw.connectionId, 'connectionId'),
    name: requireString(raw.name, 'name'),
    baseUrl: requireString(raw.baseUrl, 'baseUrl'),
    authHeaderName: nullableString(raw.authHeaderName, 'authHeaderName'),
    updatedAt: requireString(raw.updatedAt, 'updatedAt'),
  } satisfies HttpConnection
}

function decodeTestResult(raw: unknown): HttpConnectionTestResult {
  if (!isRecord(raw)) throw new Error('Malformed API connection test result')
  return { status: requireNumber(raw.status, 'status') } satisfies HttpConnectionTestResult
}

const connectionsPath = (entityId: string) =>
  `/api/v1/entities/${entityId}/integrations/http-connections`

const connectionPath = (entityId: string, connectionId: string) =>
  `${connectionsPath(entityId)}/${connectionId}`

export const httpConnectionsApi = {
  listConnections: (entityId: string, signal?: AbortSignal) =>
    request(
      connectionsPath(entityId),
      (raw) => requireArray(raw, 'connections', decodeConnection),
      { signal },
    ),
  createConnection: (entityId: string, input: HttpConnectionInput) =>
    request(connectionsPath(entityId), decodeConnection, json('POST', input)),
  updateConnection: (entityId: string, connectionId: string, input: HttpConnectionInput) =>
    request(connectionPath(entityId, connectionId), decodeConnection, json('PUT', input)),
  deleteConnection: (entityId: string, connectionId: string) =>
    requestNoContent(connectionPath(entityId, connectionId), { method: 'DELETE' }),
  testConnection: (entityId: string, connectionId: string) =>
    request(`${connectionPath(entityId, connectionId)}:test`, decodeTestResult, { method: 'POST' }),
}
